import React, { useCallback, useEffect, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import _ from 'lodash';

import {
  adminCurrencyErrorsSelector,
  adminNetworksSelector,
  createAdminCurrency,
  getAdminCurrencyLabel,
  getAdminCurrencyPayments,
  getAdminCurrencyValidator,
  getAdminEditCurrency,
  getCurrencyPaymentsType,
  isLoadingCurrencySelector,
  labelOptionsSelector,
  // paymentsSelector,
  selectedCurrencySelector,
  setAdminCurrencyErrors,
  setSelectedCurrency,
  updateAdminCurrency,
  validatorOptionsSelector,
} from 'src/store/adminCurrency';

import {
  CustomButton,
  CustomInput,
  CustomSelect,
  CustomSwitch,
  CustomUpload,
  DeleteModal,
  LanguageSelect,
  PageLoader,
  WhiteCard,
} from 'src/components';

import './currenciesForm.scss';
import { APP_LANGUAGES } from 'src/constants';
import { ReactComponent as AddIcon } from 'src/assets/overall/AddIcon.svg';
import { DeleteOutlined } from '@ant-design/icons';

export const CurrenciesForm = () => {
  const { t } = useTranslation('currenciesForm');

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const params = useParams();

  const selectedCurrency = useSelector(selectedCurrencySelector);
  const isLoading = useSelector(isLoadingCurrencySelector);
  const formError = useSelector(adminCurrencyErrorsSelector);

  const validatorOptions = useSelector(validatorOptionsSelector);
  const labelOptions = useSelector(labelOptionsSelector);
  // const payments = useSelector(paymentsSelector);
  const networks = useSelector(adminNetworksSelector);

  console.log('networks', networks);

  const [formData, setFormData] = useState({
    string_identifier: '',
    code: '',
    code_parser: '',
    code_export: '',
    code_change_export: '',
    code_payment: null,
    name: '',
    // payment_detail: '',
    image: '',
    label: '',
    payment_details_validator: '',
    num_characters: '',
    reserve: '',
    sort: '',
    active: 0,
    memo: 0,
    descriptions: {
      2: {
        name: '',
        field_name: '',
        help_field_name: '',
      },
      1: {
        name: '',
        field_name: '',
        help_field_name: '',
      },
    },
    payments: [],
    networks: [],
  });

  const [selectedTab, setSelectedTab] = useState('1');
  const [language, setLanguage] = useState(1);
  const [selectedItem, setSelectedItem] = useState({});

  const isEditMode = pathname?.includes('edit');

  useEffect(() => {
    if (isEditMode) {
      dispatch(getAdminEditCurrency(params?.id));
    }

    dispatch(getAdminCurrencyValidator());
    dispatch(getAdminCurrencyLabel());
    dispatch(getAdminCurrencyPayments());
    dispatch(getCurrencyPaymentsType());

    return () => {
      dispatch(setSelectedCurrency({}));
      dispatch(setAdminCurrencyErrors({}));
    };
  }, [dispatch, isEditMode, params?.id]);

  useEffect(() => {
    const updatedNetworks = networks?.map((item) => {
      const selectedNetwork = selectedCurrency?.networks?.find(
        (network) => network?.payment === item,
      );

      const payload = {
        payment: item,
        code: '',
        network: '',
        commission: 0,
        active: false,
        label: item,
      };

      return selectedNetwork ? { ...selectedNetwork, label: selectedNetwork.payment } : payload;
    });

    setFormData((prev) => ({ ...prev, ...selectedCurrency, networks: updatedNetworks }));
  }, [isEditMode, selectedCurrency, networks]);

  useEffect(() => {
    if (formError?.['descriptions.1.name']) {
      setLanguage(1);
    }

    if (formError?.['descriptions.2.name']) {
      setLanguage(2);
    }
  }, [formError]);

  const onChangeInput = useCallback(
    (key) => (event) => {
      setFormData((prev) => ({ ...prev, [key]: event.target.value }));

      const copiedErrors = _.cloneDeep(formError);

      delete copiedErrors[key];

      dispatch(setAdminCurrencyErrors(copiedErrors));
    },
    [dispatch, formError],
  );

  const onChangeSelect = useCallback(
    (key) => (event) => {
      setFormData((prev) => ({ ...prev, [key]: event.value }));

      const copiedErrors = _.cloneDeep(formError);

      delete copiedErrors[key];

      dispatch(setAdminCurrencyErrors(copiedErrors));
    },
    [dispatch, formError],
  );

  const onSelectFile = useCallback(
    (file) => {
      setFormData((prev) => ({ ...prev, image: file }));

      const copiedErrors = _.cloneDeep(formError);

      delete copiedErrors?.image;

      dispatch(setAdminCurrencyErrors(copiedErrors));
    },
    [dispatch, formError],
  );

  const onChangeLanguageInput = useCallback(
    (lng, key) => (event) => {
      setFormData((prev) => ({
        ...prev,
        descriptions: {
          ...prev.descriptions,
          [lng]: {
            ...prev.descriptions[lng],
            [key]: event.target.value,
          },
        },
      }));

      const copiedErrors = _.cloneDeep(formError);

      delete copiedErrors[`descriptions.${lng}.${key}`];

      dispatch(setAdminCurrencyErrors(copiedErrors));
    },
    [dispatch, formError],
  );

  const onChangeParserSwitch = useCallback(
    (key) => (event) => {
      setFormData((prev) => ({
        ...prev,
        [key]: event === true ? 1 : 0,
      }));
    },
    [],
  );

  const onChangeArrayInput = useCallback(
    (index, field) => (event) => {
      setFormData((prev) => ({
        ...prev,
        payments: prev?.payments.map((item, i) =>
          i === index ? { ...item, [field]: event.target.value } : item,
        ),
      }));
    },
    [],
  );

  const onChangeNetworksArrayInput = useCallback(
    (index, field) => (event) => {
      setFormData((prev) => ({
        ...prev,
        networks: prev?.networks.map((item, i) =>
          i === index ? { ...item, [field]: event.target.value } : item,
        ),
      }));

      const copiedErrors = _.cloneDeep(formError);

      delete copiedErrors[`networks.${index}.${field}`];

      dispatch(setAdminCurrencyErrors(copiedErrors));
    },
    [dispatch, formError],
  );

  const onChangeNetworkSwitch = useCallback(
    (index, field) => (event) => {
      setFormData((prev) => ({
        ...prev,
        networks: prev?.networks.map((item, i) =>
          i === index ? { ...item, [field]: event } : item,
        ),
      }));
    },
    [],
  );

  const onClickTab = useCallback(
    (tab) => () => {
      setSelectedTab(tab);
    },
    [],
  );

  const onClickAddRequisite = useCallback(() => {
    const payload = {
      id: formData?.payments?.length + 1,
      payment: '',
      memo: '',
    };

    setFormData((prev) => ({ ...prev, payments: [...prev?.payments, payload] }));
  }, [formData?.payments?.length]);

  const onClickDeleteRequisite = useCallback(
    (item) => () => {
      setSelectedItem(item);
    },
    [],
  );

  const onClickCancel = useCallback(() => {
    setSelectedItem({});
  }, []);

  const onClickConfirm = useCallback(() => {
    const rejected = _.reject(formData?.payments, (item) => selectedItem?.id === item.id);

    setFormData((prev) => ({ ...prev, payments: rejected }));

    setSelectedItem({});
  }, [formData?.payments, selectedItem?.id]);

  const onSubmit = useCallback(
    async (event) => {
      event.preventDefault();

      if (isEditMode) {
        await dispatch(updateAdminCurrency({ id: params?.id, payload: formData, navigate }));
        return;
      }

      await dispatch(createAdminCurrency({ navigate, payload: formData }));
    },
    [dispatch, navigate, formData, isEditMode, params?.id],
  );

  const selectedLanguage = APP_LANGUAGES?.find((item) => item.id === language);

  return (
    <div className="currencies-form">
      {!_.isEmpty(selectedItem) ? (
        <DeleteModal title="Вы уверены?" onCancel={onClickCancel} onConfirm={onClickConfirm} />
      ) : null}

      <WhiteCard>
        {isLoading ? <PageLoader /> : null}

        <h3>
          {t(`adminCurrencies:${!isEditMode ? 'create' : 'edit'}`)}{' '}
          <div className="exchange-directions-form__tabs">
            <CustomButton
              title={t('currency')}
              variant={selectedTab === '1' ? 'filled' : 'outlined'}
              onClick={onClickTab('1')}
            />
            <CustomButton
              title={t('requisites')}
              variant={selectedTab === '2' ? 'filled' : 'outlined'}
              onClick={onClickTab('2')}
            />
            <CustomButton
              title={t('Платежные системы')}
              variant={selectedTab === '3' ? 'filled' : 'outlined'}
              onClick={onClickTab('3')}
            />
          </div>
          <LanguageSelect language={selectedLanguage?.id} setLanguage={setLanguage} />
        </h3>
        <form onSubmit={onSubmit}>
          <div
            className="currencies-form__content"
            style={{
              gridTemplateColumns: selectedTab === '1' || selectedTab === '3' ? '1fr 1fr' : '1fr',
            }}
          >
            {selectedTab === '1' && (
              <>
                {/* <CustomInput
                  label={t('identifier')}
                  value={formData.string_identifier}
                  onChange={onChangeInput('string_identifier')}
                  errorMessage={formError?.string_identifier}
                /> */}
                <CustomInput
                  label={t('code')}
                  value={formData.code}
                  onChange={onChangeInput('code')}
                  errorMessage={formError?.code}
                />
                <CustomInput
                  label={t('Код для парсера')}
                  value={formData.code_parser}
                  onChange={onChangeInput('code_parser')}
                  errorMessage={formError?.code_parser}
                />
                <CustomInput
                  type="number"
                  label={t('reserve')}
                  value={formData.reserve}
                  onChange={onChangeInput('reserve')}
                  errorMessage={formError?.reserve}
                />
                <CustomInput
                  type="number"
                  label={t('numbersAfter')}
                  value={formData.num_characters}
                  onChange={onChangeInput('num_characters')}
                  errorMessage={formError?.num_characters}
                />
                <CustomSelect
                  label={t('mark')}
                  value={formData.label}
                  options={labelOptions}
                  onChange={onChangeSelect('label')}
                />
                {/* <CustomInput
                  label={t('paymentReq')}
                  value={formData.payment_detail}
                  onChange={onChangeInput('payment_detail')}
                />
                <CustomInput
                  label={t('paymentReq')}
                  value={formData.payment_detail}
                  onChange={onChangeInput('payment_detail')}
                /> */}
                <CustomInput
                  label={t('codeExport')}
                  value={formData.code_export}
                  onChange={onChangeInput('code_export')}
                  errorMessage={formError?.code_export}
                />
                <CustomInput
                  label={t('codeChangeExport')}
                  value={formData.code_change_export}
                  onChange={onChangeInput('code_change_export')}
                  errorMessage={formError?.code_change_export}
                />
                <CustomSelect
                  label={t('paymentValidator')}
                  value={formData?.payment_details_validator}
                  options={validatorOptions}
                  onChange={onChangeSelect('payment_details_validator')}
                />
                {/* <CustomSelect
                  label={t('West Wallet')}
                  value={formData?.code_payment}
                  options={[{ label: 'Не выбрано', value: null }, ...payments]}
                  onChange={onChangeSelect('code_payment')}
                /> */}
                <CustomUpload label={t('icon')} value={formData?.image} onChange={onSelectFile} />
                <CustomInput
                  label={t('sort')}
                  value={formData.sort}
                  errorMessage={formError?.sort}
                  onChange={onChangeInput('sort')}
                  type="number"
                />
                <CustomInput
                  label={`${t('nameRu')} (${selectedLanguage?.language?.toUpperCase()})`}
                  value={formData.descriptions[language]?.name}
                  errorMessage={
                    formError?.['descriptions.1.name'] || formError?.['descriptions.2.name']
                  }
                  onChange={onChangeLanguageInput(language, 'name')}
                />
                <CustomInput
                  label={`${t('fieldRu')} (${selectedLanguage?.language?.toUpperCase()})`}
                  value={formData.descriptions[language]?.field_name}
                  onChange={onChangeLanguageInput(language, 'field_name')}
                />
                {/* <CustomInput
                  label={`${t('infoRu')} (${selectedLanguage?.language?.toUpperCase()})`}
                  value={formData.descriptions[language]?.help_field_name}
                  onChange={onChangeLanguageInput(language, 'help_field_name')}
                /> */}
              </>
            )}

            {selectedTab === '2' && (
              <>
                {formData?.payments?.map((item, index) => (
                  <div className="currencies-form__row" key={index}>
                    <CustomInput
                      label={`${t('paymentReq')} ${index + 1}`}
                      value={item?.payment}
                      onChange={onChangeArrayInput(index, 'payment')}
                      errorMessage={formError?.code_export}
                    />
                    {formData?.memo ? (
                      <CustomInput
                        label={`${t('memo')} ${index + 1}`}
                        value={item?.memo}
                        onChange={onChangeArrayInput(index, 'memo')}
                        errorMessage={formError?.code_export}
                      />
                    ) : null}
                    <DeleteOutlined
                      style={{ fontSize: '24px' }}
                      onClick={onClickDeleteRequisite(item)}
                    />
                  </div>
                ))}
              </>
            )}

            {selectedTab === '3' && (
              <>
                {formData?.networks?.map((item, index) => (
                  <div className="currencies-form__networks" key={index}>
                    <CustomInput
                      label={`${item.label} Название`}
                      value={item?.payment}
                      onChange={onChangeNetworksArrayInput(index, 'payment')}
                      errorMessage={formError?.[`networks.${index}.payment`]}
                    />
                    <CustomInput
                      label={`${item.label} Код`}
                      value={item?.code}
                      onChange={onChangeNetworksArrayInput(index, 'code')}
                      errorMessage={formError?.[`networks.${index}.code`]}
                    />
                    <CustomInput
                      label={`${item.label} Network / Сеть`}
                      value={item?.network}
                      onChange={onChangeNetworksArrayInput(index, 'network')}
                      errorMessage={formError?.[`networks.${index}.network`]}
                    />
                    <CustomInput
                      label={`${item.label} Комиссия`}
                      value={item?.commission}
                      onChange={onChangeNetworksArrayInput(index, 'commission')}
                      errorMessage={formError?.[`networks.${index}.commission`]}
                      type="number"
                    />

                    <div style={{ marginTop: 10 }}>
                      <CustomSwitch
                        label={t('useIdentifier')}
                        isActive={item?.active}
                        onChange={onChangeNetworkSwitch(index, 'active')}
                      />
                    </div>
                  </div>
                ))}
              </>
            )}
          </div>

          {selectedTab === '1' ? (
            <div className="exchange-directions-form__switch">
              {/* <CustomSwitch
                label={t('useIdentifier')}
                isActive={formData.active}
                onChange={onChangeParserSwitch('active')}
              /> */}
              <CustomSwitch
                label={t('memoRequisites')}
                isActive={formData.memo}
                onChange={onChangeParserSwitch('memo')}
              />
            </div>
          ) : null}

          {selectedTab === '2' ? (
            <div>
              <CustomButton
                title={t('title:addField')}
                type="button"
                icon={<AddIcon />}
                iconPosition="right"
                onClick={onClickAddRequisite}
              />
            </div>
          ) : null}

          <CustomButton variant="filled" title={t('title:save')} type="submit" />
        </form>
      </WhiteCard>
    </div>
  );
};
